import type { Id } from "../primitives";

/**
 * How a mention lives inside a comment's text (#448).
 *
 * A mention is stored as `@[<userId>]` in the body, not as a typed name. That
 * keeps it attached to the person when they are renamed, lets the server tell
 * exactly who was mentioned without guessing from text, and lets a screen show
 * the name a person has now. What someone reads and edits is `@Name`; the
 * conversion between the two is here, shared, so the screen and the server
 * cannot disagree about what a token is.
 *
 * Ids are strings (`Id`), and a fixture's look like `usr_0001`, so a token
 * accepts letters, digits, `_` and `-`. The API only accepts ones that name an
 * active user.
 */
const TOKEN = /@\[([A-Za-z0-9_-]+)\]/g;

/** A piece of a body, in order: text to show as written, or a mention to resolve. */
export type BodySegment =
  { kind: "text"; text: string } | { kind: "mention"; userId: Id };

/** Every user a body mentions, once each, in the order they first appear. */
export function mentionedUserIds(body: string): Id[] {
  const found: Id[] = [];
  for (const match of body.matchAll(TOKEN)) {
    const userId = match[1];
    if (userId !== undefined && !found.includes(userId)) found.push(userId);
  }
  return found;
}

/** A body split into text and mentions, for rendering. Text is never altered. */
export function bodySegments(body: string): BodySegment[] {
  const segments: BodySegment[] = [];
  let last = 0;
  for (const match of body.matchAll(TOKEN)) {
    const userId = match[1];
    if (userId === undefined) continue;
    if (match.index > last) {
      segments.push({ kind: "text", text: body.slice(last, match.index) });
    }
    segments.push({ kind: "mention", userId });
    last = match.index + match[0].length;
  }
  if (last < body.length) {
    segments.push({ kind: "text", text: body.slice(last) });
  }
  return segments;
}

/**
 * Someone the author picked from the `@` list: who, the name shown, and where
 * the `@` is in the draft.
 *
 * Where, not just who, because names are not unique. Two colleagues can both be
 * "Rahul Sharma", and a draft that said only `@Rahul Sharma` twice could not be
 * turned back into two different people by looking at its text. The pick holds
 * the id of the person actually chosen, at the place they were chosen, and is
 * never looked up by name again.
 */
export interface MentionPick {
  userId: Id;
  name: string;
  /** Index of the `@` in the draft text. */
  start: number;
}

const WORD = /[\p{L}\p{N}_]/u;

/** The end of a pick's `@Name` in the draft. */
function endOf(pick: MentionPick): number {
  return pick.start + 1 + pick.name.length;
}

/**
 * A stored body as a draft to edit: each `@[id]` becomes `@Name`, using the
 * names given, and each is remembered as a pick at its place in the new text. A
 * token whose id has no name is left as it is, with no pick, so saving the edit
 * unchanged cannot silently lose a mention.
 */
export function editableDraft(
  body: string,
  names: ReadonlyMap<Id, string>,
): { text: string; picks: MentionPick[] } {
  let text = "";
  let last = 0;
  const picks: MentionPick[] = [];
  for (const match of body.matchAll(TOKEN)) {
    const userId = match[1];
    if (userId === undefined) continue;
    const name = names.get(userId);
    text += body.slice(last, match.index);
    last = match.index + match[0].length;
    if (name === undefined) {
      text += match[0];
      continue;
    }
    picks.push({ userId, name, start: text.length });
    text += `@${name}`;
  }
  return { text: text + body.slice(last), picks };
}

/**
 * Where the picks are after the draft changed from `before` to `after`.
 *
 * The change is taken to be one edit — the stretch between what the two texts
 * share at their start and what they share at their end — which is what typing,
 * deleting, pasting and replacing a selection all are. A pick wholly before it
 * stays; one wholly after it moves by the change in length; one the edit
 * touched, even by a letter, is gone: it is no longer the name that was picked.
 */
export function shiftPicks(
  before: string,
  after: string,
  picks: readonly MentionPick[],
): MentionPick[] {
  if (before === after) return [...picks];
  const shortest = Math.min(before.length, after.length);
  let prefix = 0;
  while (prefix < shortest && before[prefix] === after[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < shortest - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const editedTo = before.length - suffix;
  const delta = after.length - before.length;
  return picks.flatMap((pick) => {
    if (endOf(pick) <= prefix) return [pick];
    if (pick.start >= editedTo) return [{ ...pick, start: pick.start + delta }];
    return [];
  });
}

/**
 * A draft as typed back to a body with tokens: each pick still standing at its
 * place becomes `@[id]`.
 *
 * Only picked people become mentions. `@Asha` typed by hand, without choosing
 * her from the list, stays plain text — a mention is always a person somebody
 * selected, never an accident of spelling — and each pick resolves to its own
 * id, so two people with one name are two mentions. A pick is dropped, and its
 * text left as written, when what is at its place is no longer `@Name` on its
 * own: the name was changed, ran on into a longer word (`@Ann` into
 * `@Annabel`), or sits against a letter on its left (an email address).
 */
export function tokenisedBody(
  text: string,
  picks: readonly MentionPick[],
): string {
  let result = text;
  let boundary = text.length;
  // From the end, so a replacement does not move the picks still to come.
  const ordered = [...picks].sort((a, b) => b.start - a.start);
  for (const pick of ordered) {
    const end = endOf(pick);
    if (pick.name === "" || pick.start < 0 || end > boundary) continue;
    if (text.slice(pick.start, end) !== `@${pick.name}`) continue;
    const before = text[pick.start - 1];
    const after = text[end];
    if (before !== undefined && WORD.test(before)) continue;
    if (after !== undefined && WORD.test(after)) continue;
    result = `${result.slice(0, pick.start)}@[${pick.userId}]${result.slice(end)}`;
    boundary = pick.start;
  }
  return result;
}
